import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useFMContext } from '@pflegenavi/frontend/family-member-context';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import type { FormikHelpers } from 'formik';
import { useFormik } from 'formik';
import { useSetupMangopayDirectDebitValidationSchema } from '@pflegenavi/shared/frontend';
import type {
  Country,
  SetupMangoPayDirectDebitBody,
  SetupMangoPayDirectDebitResponse,
} from '@pflegenavi/frontend/api-nursing-home';
import { MangopayBankAccountOwner } from '@pflegenavi/shared/api';
import { useInitializeFM } from '../../../hooks/useInitializeFM';
import { SetupMangopayDirectDebitView } from './SetupMangopayDirectDebitView';

interface SetupMangopayDirectDebitProps {
  countries: Country[];
  owner: MangopayBankAccountOwner | undefined;
  mandateActive: boolean;
  bankAccount: {
    iban: string;
    owner_name: string;
    owner_address: {
      city: string;
      address_line_1: string;
      address_line_2: string;
      region: string;
      country: string;
      postal_code: string;
    };
  };
  setupMandate: (
    params: SetupMangoPayDirectDebitBody
  ) => Promise<SetupMangoPayDirectDebitResponse>;
  PaymentInitiationInfoComponent?: React.ComponentType;
  isDisabled?: boolean;
}

export interface ISetupMangopayDirectDebitValues {
  ownerName: string | undefined;
  iban: string | undefined;
  city: string | undefined;
  addressLine1: string | undefined;
  addressLine2: string | undefined;
  country: string | undefined;
  postalCode: string | undefined;
  // region removed as it's not necessary
}

export const SetupMangopayDirectDebit: FC<SetupMangopayDirectDebitProps> = ({
  countries,
  owner,
  bankAccount,
  mandateActive,
  setupMandate,
  PaymentInitiationInfoComponent,
  isDisabled = false,
}) => {
  const { familyMember } = useFMContext();
  const { t } = useTranslation();
  const { selectedResident } = useInitializeFM();

  const [redirectWarningOpen, setRedirectWarningOpen] = useState(false);

  const validationSchema = Yup.object(
    useSetupMangopayDirectDebitValidationSchema()
  );

  const prefillOwnerName = useMemo(() => {
    if (owner === MangopayBankAccountOwner.FamilyMember) {
      return [familyMember?.given_name, familyMember?.family_name]
        .filter(Boolean)
        .join(' ');
    } else if (owner === MangopayBankAccountOwner.Resident) {
      return [selectedResident?.firstName, selectedResident?.lastName]
        .filter(Boolean)
        .join(' ');
    } else {
      return undefined;
    }
  }, [selectedResident, familyMember, owner]);

  // The user needs to select the owner first.
  const disabled = owner === undefined || mandateActive || isDisabled;

  const initialValues: ISetupMangopayDirectDebitValues = useMemo(() => {
    return owner !== undefined
      ? {
          ownerName: bankAccount.owner_name || prefillOwnerName,
          iban: bankAccount.iban,
          city: bankAccount.owner_address.city,
          addressLine1: bankAccount.owner_address.address_line_1,
          addressLine2: bankAccount.owner_address.address_line_2,
          country: bankAccount.owner_address.country,
          postalCode: bankAccount.owner_address.postal_code,
        }
      : {
          ownerName: undefined,
          iban: undefined,
          city: undefined,
          addressLine1: undefined,
          addressLine2: undefined,
          country: undefined,
          postalCode: undefined,
        };
  }, [
    bankAccount.owner_name,
    prefillOwnerName,
    bankAccount.iban,
    bankAccount.owner_address.city,
    bankAccount.owner_address.address_line_1,
    bankAccount.owner_address.address_line_2,
    bankAccount.owner_address.country,
    bankAccount.owner_address.postal_code,
    owner,
  ]);

  const handleSubmit = useCallback(
    async (
      values: ISetupMangopayDirectDebitValues,
      actions: FormikHelpers<ISetupMangopayDirectDebitValues>
    ) => {
      if (
        !values.ownerName ||
        !values.iban ||
        !values.city ||
        !values.addressLine1 ||
        !values.country ||
        !values.postalCode
      ) {
        throw new Error('Form is not valid');
      }
      actions.setSubmitting(true);
      const { data } = await setupMandate({
        bank_account: {
          iban: values.iban,
          owner_name: values.ownerName,
          owner_address: {
            city: values.city,
            address_line_1: values.addressLine1,
            address_line_2: values.addressLine2,
            country: values.country,
            postal_code: values.postalCode,
          },
        },
      });

      if (data.mandate.document_url) {
        window.location.href = data.mandate.document_url;
      }
      actions.setSubmitting(false);

      actions.resetForm({ values });
    },
    [setupMandate]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema,
    validateOnChange: true,
    validateOnMount: true,
  });

  const countryOptions = useMemo(() => {
    return countries.map((country) => ({
      label: t(`countries.${country.iso_code}`),
      value: country.iso_code,
    }));
  }, [countries, t]);

  const onChangeCountry = useCallback(
    (newCountryId: string) => {
      if (formik.values.country === newCountryId) {
        return;
      }

      formik.setValues((values) => ({
        ...values,
        country: newCountryId,
      }));
      formik.setFieldError('country', undefined);
    },
    [formik]
  );

  const onCountryBlur = useCallback(() => {
    formik.setFieldTouched('country', true);
  }, [formik]);

  return (
    <SetupMangopayDirectDebitView
      formik={formik}
      disabled={disabled}
      countryOptions={countryOptions}
      onChangeCountry={onChangeCountry}
      onCountryBlur={onCountryBlur}
      redirectWarningOpen={redirectWarningOpen}
      setRedirectWarningOpen={setRedirectWarningOpen}
      PaymentInitiationInfoComponent={PaymentInitiationInfoComponent}
    />
  );
};
